import { useEffect, useMemo, useState } from "react";
import { useDeleteFile, useInputs, useProjectFile, useSaveFile } from "../../api/hooks";
import { usePendingOpenFile, usePublishActiveFile } from "../../app/activeFile";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, JsonTree, Modal, Spinner, errText } from "../../components/ui";
import { CodeEditor } from "../../editor/CodeEditor";

const TEMPLATES: Record<string, string> = {
  yaml: "# Constants and lookup tables reachable as ctx.inputs.<file>.<key>\nlimits:\n  n1_max: 104.0\n  egt_max: 950\n",
  csv: "tail,engine_1_serial,engine_2_serial\nXA-HPY,ESN-0001,ESN-0002\n",
  json: '{\n  "airports": {"LZIB": {"name": "Bratislava", "elev_ft": 436}}\n}\n',
};

export function InputsPage() {
  const projectId = useSession((s) => s.projectId);
  const toast = useSession((s) => s.toast);
  const inputs = useInputs(projectId);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const file = useProjectFile(projectId, selected);
  const save = useSaveFile(projectId);
  const del = useDeleteFile(projectId);

  useEffect(() => {
    if (!selected && inputs.data?.files.length) setSelected(inputs.data.files[0].path);
  }, [inputs.data, selected]);
  useEffect(() => setDraft(null), [selected, file.data?.modified]);
  usePublishActiveFile(selected);
  usePendingOpenFile("inputs/", setSelected);

  const text = draft ?? file.data?.content ?? "";
  const dirty = draft !== null && draft !== (file.data?.content ?? "");
  const stem = selected ? selected.split("/").pop()!.replace(/\.[^.]+$/, "") : null;
  const refs = useMemo(() => (stem ? inputs.data?.references[stem] ?? [] : []), [inputs.data, stem]);
  const resolved = stem ? inputs.data?.resolved[stem] : undefined;

  const doSave = () => {
    if (!selected || draft === null) return;
    save.mutate(
      { path: selected, content: draft },
      { onSuccess: () => toast("Saved", "success"), onError: (e) => toast(errText(e), "error") },
    );
  };

  if (!projectId) return <Empty title="No project selected" />;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Inputs</h1>
        <span className="sub">constants, lookup tables and per-flight metadata exposed as ctx.inputs</span>
        <span className="spacer" />
        <button className="btn" onClick={() => setCreating(true)}>
          ＋ New input file
        </button>
        <button className="btn primary" disabled={!dirty || save.isPending} onClick={doSave} title="Ctrl+S">
          Save
        </button>
      </div>
      <div className="page-body">
        <Split id="inputs" sizes={[220, "1fr", 360]} minSizes={[140, 320, 220]}>
          <div className="pane">
            <div className="pane-head">Files</div>
            <div className="list">
              {inputs.data?.files.map((f) => (
                <div key={f.path} className={`list-item${f.path === selected ? " sel" : ""}`} onClick={() => setSelected(f.path)}>
                  <span className="name mono">{f.name}</span>
                  <span className="faint small">{f.name.split(".").pop()}</span>
                </div>
              ))}
              {inputs.data && !inputs.data.files.length ? <div className="empty small">No input files yet.</div> : null}
            </div>
            <div className="pane-head" style={{ marginTop: 8 }}>
              Flight fields
            </div>
            <div className="pane-body small">
              {inputs.data?.flight_fields.length ? (
                inputs.data.flight_fields.map((f) => (
                  <div key={f.name} className="row">
                    <code>flight.{f.name}</code>
                    <span className="faint">{f.type}</span>
                    {f.infer ? <span className="badge">{f.infer}</span> : null}
                  </div>
                ))
              ) : (
                <span className="dim">none declared (project.yaml flight_fields)</span>
              )}
            </div>
          </div>
          <div className="pane">
            {selected && file.data ? (
              <>
                <div className="pane-head">
                  <span className="mono" style={{ textTransform: "none" }}>
                    {selected}
                  </span>
                  {dirty ? <span className="badge warn">modified</span> : null}
                  <span className="spacer" />
                  <button
                    className="btn ghost sm"
                    onClick={() => {
                      if (confirm(`Delete ${selected}?`))
                        del.mutate(selected, {
                          onSuccess: () => setSelected(null),
                          onError: (e) => toast(errText(e), "error"),
                        });
                    }}
                  >
                    delete
                  </button>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <CodeEditor path={`${projectId}/${selected}`} value={text} onChange={setDraft} onSave={doSave} />
                </div>
              </>
            ) : inputs.isLoading ? (
              <div className="pane-body">
                <Spinner label="loading" />
              </div>
            ) : (
              <Empty title="Select or create an input file">
                Every file in <code>inputs/</code> becomes <code>ctx.inputs.&lt;stem&gt;</code>: YAML/JSON as nested namespaces, CSV as
                tables with <code>.by(key)</code>, <code>.where(...)</code>, <code>.column(...)</code>.
              </Empty>
            )}
          </div>
          <div className="pane">
            <div className="pane-head">Resolved value</div>
            <div className="pane-body tree" style={{ flex: "1 1 0", overflow: "auto" }}>
              {resolved !== undefined ? <JsonTree name={`inputs.${stem}`} value={resolved} /> : <span className="dim">—</span>}
            </div>
            <div className="pane-head">Referenced by</div>
            <div className="pane-body small" style={{ flex: "0 1 auto", maxHeight: "45%", overflow: "auto" }}>
              {refs.length ? (
                refs.map((r, i) => (
                  <div key={i} className="row">
                    <code className="dim">
                      {r.file}:{r.line}
                    </code>
                    <code>{r.expr}</code>
                  </div>
                ))
              ) : (
                <span className="dim">not referenced by triggers, derived parameters or templates</span>
              )}
            </div>
          </div>
        </Split>
      </div>
      {creating ? (
        <NewInputDialog
          onClose={() => setCreating(false)}
          onCreate={(name, content) =>
            save.mutate(
              { path: `inputs/${name}`, content },
              {
                onSuccess: () => {
                  setSelected(`inputs/${name}`);
                  setCreating(false);
                },
                onError: (e) => toast(errText(e), "error"),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

function NewInputDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string, content: string) => void }) {
  const [stem, setStem] = useState("");
  const [ext, setExt] = useState("yaml");
  const ok = /^[a-zA-Z_][\w-]*$/.test(stem);
  return (
    <Modal
      title="New input file"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!ok} onClick={() => onCreate(`${stem}.${ext}`, TEMPLATES[ext])}>
            Create
          </button>
        </>
      }
    >
      <div className="row">
        <label className="field" style={{ flex: 1 }}>
          <span>Name (becomes ctx.inputs.&lt;name&gt;)</span>
          <input className="inp" autoFocus value={stem} onChange={(e) => setStem(e.target.value)} placeholder="fleet_limits" />
        </label>
        <label className="field">
          <span>Type</span>
          <select className="inp" value={ext} onChange={(e) => setExt(e.target.value)}>
            <option value="yaml">yaml — constants</option>
            <option value="csv">csv — lookup table</option>
            <option value="json">json</option>
          </select>
        </label>
      </div>
    </Modal>
  );
}
